import { useState, useEffect, useRef } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { IoIosArrowRoundBack } from "react-icons/io"
import { FaTrash, FaUpload } from "react-icons/fa"
import { FiUpload } from "react-icons/fi"
import api, { fetchAll } from "../../utils/api"
import { ErrorNotification, SuccessNotification } from "../../utils"
import { useForm } from "react-hook-form"
import ImageSourceUploadButton from "../../components/ImageSourceUploadButton"
import { trimFormValue, personNamePatternRule } from "../../utils/formValues"
import {
  useLocationCascade,
  fetchDistrictsByRegion,
  fetchCommunitiesByDistrict,
} from "../../hooks/useLocations"

const trimField = { setValueAs: trimFormValue }

const steps = [
  "Group Information",
  "Farm Information",
]

export default function UpdateFarmer() {
  const history = useHistory()
  const location = useLocation()
  const farmer = location.state?.farmer
  const [loading, setLoading] = useState(false)
  const [groups, setGroups] = useState([])
  const [step, setStep] = useState(0)
  const [profileImagePreview, setProfileImagePreview] = useState(null)
  const [frontImagePreview, setFrontImagePreview] = useState(null)
  const [backImagePreview, setBackImagePreview] = useState(null)
  const submittingRef = useRef(false)
  const locationPrefillDone = useRef(false)

  const toOptionLabel = (option) => {
    if (option == null) return ""
    if (typeof option === "object") return option.name || option.label || ""
    return String(option)
  }

  const normalizeValue = (value, options) => {
    if (!value || !options || !Array.isArray(options)) return value || ""

    const labels = options.map(toOptionLabel).filter(Boolean)
    if (labels.includes(value)) return value

    const lowerValue = value.toLowerCase().trim()
    const match = labels.find((label) => {
      const lowerOpt = label.toLowerCase()
      return lowerOpt === lowerValue || lowerOpt.includes(lowerValue) || lowerValue.includes(lowerOpt)
    })

    return match || value
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      contact: "",
      dob: "",
      gender: "",
      education: "",
      region: "",
      district: "",
      community: "",
      group: "",
      front_image: "",
      back_image: "",
      profile_image: "",
    },
  })

  const region = watch("region")
  const district = watch("district")
  const community = watch("community")
  const group = watch("group")

  const {
    regionNames,
    districts,
    communities,
    isLoading: locationLoading,
  } = useLocationCascade(region, district)

  const handleRegionChange = (value) => {
    setValue("region", value, { shouldValidate: true })
    setValue("district", "", { shouldValidate: true })
    setValue("community", "", { shouldValidate: true })
  }

  const handleDistrictChange = (value) => {
    setValue("district", value, { shouldValidate: true })
    setValue("community", "", { shouldValidate: true })
  }

  // Pre-fill form with farmer data from history state
  useEffect(() => {
    if (!farmer) {
      ErrorNotification("No farmer data found")
      history.goBack()
      return
    }

    locationPrefillDone.current = false

    reset({
      firstname: farmer.firstname || "",
      lastname: farmer.lastname || "",
      email: farmer.email || "",
      contact: farmer.phone || "",
      dob: farmer.dob || "",
      gender: farmer.gender || "",
      education: farmer.education_level || "",
      region: "",
      district: "",
      community: "",
      group: farmer.farmer_group?.[0]?.id?.toString() || "",
    })

    if (farmer.profile_image) setProfileImagePreview(farmer.profile_image)
    if (farmer.ghana_card_image_front) setFrontImagePreview(farmer.ghana_card_image_front)
    if (farmer.ghana_card_image_back) setBackImagePreview(farmer.ghana_card_image_back)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmer?.id])

  // Prefill location once region data is available (v2 name lookups)
  useEffect(() => {
    if (!farmer || !regionNames.length || locationPrefillDone.current) return

    let cancelled = false

    const prefillLocation = async () => {
      const normalizedRegion = normalizeValue(farmer.region, regionNames) || farmer.region || ""
      setValue("region", normalizedRegion, { shouldValidate: true })

      if (!normalizedRegion) {
        locationPrefillDone.current = true
        return
      }

      try {
        const dists = await fetchDistrictsByRegion(normalizedRegion)
        if (cancelled) return
        const normalizedDistrict =
          normalizeValue(farmer.district, dists) || farmer.district || ""
        setValue("district", normalizedDistrict, { shouldValidate: true })

        if (normalizedDistrict) {
          const comms = await fetchCommunitiesByDistrict(normalizedDistrict)
          if (cancelled) return
          const normalizedCommunity =
            normalizeValue(farmer.community, comms) || farmer.community || ""
          setValue("community", normalizedCommunity, { shouldValidate: true })
        }
      } catch (error) {
        console.error("Failed to prefill farmer location:", error)
      } finally {
        if (!cancelled) locationPrefillDone.current = true
      }
    }

    prefillLocation()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmer?.id, regionNames])

  // Fetch groups
  useEffect(() => {
    async function fetchGroups() {
      try {
        const groupsData = await fetchAll('groups/')
        if (groupsData) {
          setGroups(groupsData)
        }
      } catch { }
    }
    fetchGroups()
  }, [])

  const handleProfileImageFile = (file) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setProfileImagePreview(reader.result)
      setValue('profile_image', file, { shouldValidate: true })
    }
    reader.readAsDataURL(file)
  }

  const handleGhanaCardFrontFile = (file) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setFrontImagePreview(reader.result)
      setValue('front_image', file, { shouldValidate: true })
    }
    reader.readAsDataURL(file)
  }

  const handleGhanaCardBackFile = (file) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setBackImagePreview(reader.result)
      setValue('back_image', file, { shouldValidate: true })
    }
    reader.readAsDataURL(file)
  }

  const onSubmit = async (data) => {
    if (submittingRef.current) return
    submittingRef.current = true
    setLoading(true)
    try {
      // Build the main payload as JSON
      const payload = {
        firstname: trimFormValue(data.firstname),
        lastname: trimFormValue(data.lastname),
        email: trimFormValue(data.email) || "",
        phone: data.contact?.trim() || "",
        dob: data.dob || null,
        gender: data.gender,
        education_level: data.education || null,
        region: data.region,
        district: data.district,
        community: data.community,
        farmer_group_ids: data.group ? [parseInt(data.group)] : [],
      }

      // Send main data as JSON
      await api.patch(`farmers/${farmer.id}/`, JSON.stringify(payload), {
        headers: { 'Content-Type': 'application/json' }
      })

      // If there are images to upload, send them separately
      if (data.profile_image || data.front_image || data.back_image) {
        const imageFormData = new FormData()

        if (data.profile_image) {
          imageFormData.append('profile_image', data.profile_image)
        }
        if (data.front_image) {
          imageFormData.append('ghana_card_image_front', data.front_image)
        }
        if (data.back_image) {
          imageFormData.append('ghana_card_image_back', data.back_image)
        }

        await api.patch(`farmers/${farmer.id}/`, imageFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      }

      setLoading(false)
      submittingRef.current = false
      SuccessNotification("Farmer updated successfully")
      history.goBack()
    } catch (e) {
      console.error('Update error:', e.response?.data)
      ErrorNotification(e.response?.data || "Failed to update farmer")
      setLoading(false)
      submittingRef.current = false
    }
  }

  if (!farmer) {
    return (
      <div className="w-full h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellowcolor"></div>
      </div>
    )
  }

  return (
    <div className="w-full lg:px-10 px-2 h-full py-6">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => history.goBack()} className="mb-3 flex flex-row items-center">
          <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
          <p className="text-sm font-light capitalize dark:text-white">
            Back
          </p>
        </button>
      </div>

      <h1 className="text-lg font-bold capitalize mb-5 dark:text-white">
        Update Farmer
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white dark:bg-dark h-full px-4 py-2 mt-2 rounded-lg">
        <div className="h-[80vh] w-full py-3 overflow-y-auto">
          <h1 className="text-sm font-bold text-yellowcolor capitalize mb-5">
            {steps[step]}
          </h1>

          <div className="w-full h-full space-y-4">
            <div className="w-full space-y-3">
              <label className="text-sm font-normal capitalize dark:text-white">
                Profile Image
              </label>

              <div className="w-full h-full">
                {profileImagePreview ? (
                  <div className="relative w-28 h-28">
                    <img
                      src={profileImagePreview}
                      alt="profile"
                      className="w-28 h-28 rounded-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setProfileImagePreview(null)
                        setValue('profile_image', null)
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-28 h-28">
                    <svg width="132" height="132" viewBox="0 0 132 132" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g filter="url(#filter0_dd_1545_118337)">
                        <rect x="16" y="4" width="100" height="100" rx="50" fill="white" />
                        <rect x="14" y="2" width="104" height="104" rx="52" stroke="#E2E2E2" strokeWidth="4" />
                        <path d="M66.3701 56.9048C66.1542 56.874 65.8767 56.874 65.6301 56.9048C60.2034 56.7198 55.8867 52.2798 55.8867 46.8223C55.8867 41.2415 60.3884 36.709 66.0001 36.709C71.5809 36.709 76.1134 41.2415 76.1134 46.8223C76.0826 52.2798 71.7967 56.7198 66.3701 56.9048Z" stroke="#E2E2E2" strokeWidth="3.46875" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M86.7821 77.2555C81.2937 82.2814 74.0171 85.3339 66.0004 85.3339C57.9837 85.3339 50.7071 82.2814 45.2188 77.2555C45.5271 74.3572 47.3771 71.5205 50.6762 69.3005C59.1246 63.6889 72.9379 63.6889 81.3246 69.3005C84.6237 71.5205 86.4737 74.3572 86.7821 77.2555Z" stroke="#E2E2E2" strokeWidth="3.46875" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M65.9993 85.3346C83.0281 85.3346 96.8327 71.5301 96.8327 54.5013C96.8327 37.4725 83.0281 23.668 65.9993 23.668C48.9706 23.668 35.166 37.4725 35.166 54.5013C35.166 71.5301 48.9706 85.3346 65.9993 85.3346Z" stroke="#E2E2E2" strokeWidth="3.46875" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                      <defs>
                        <filter id="filter0_dd_1545_118337" x="0" y="0" width="132" height="132" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                          <feFlood floodOpacity="0" result="BackgroundImageFix" />
                          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                          <feMorphology radius="2" operator="erode" in="SourceAlpha" result="effect1_dropShadow_1545_118337" />
                          <feOffset dy="4" />
                          <feGaussianBlur stdDeviation="3" />
                          <feColorMatrix type="matrix" values="0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.03 0" />
                          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1545_118337" />
                          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                          <feMorphology radius="4" operator="erode" in="SourceAlpha" result="effect2_dropShadow_1545_118337" />
                          <feOffset dy="12" />
                          <feGaussianBlur stdDeviation="8" />
                          <feColorMatrix type="matrix" values="0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.08 0" />
                          <feBlend mode="normal" in2="effect1_dropShadow_1545_118337" result="effect2_dropShadow_1545_118337" />
                          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_1545_118337" result="shape" />
                        </filter>
                      </defs>
                    </svg>
                  </div>
                )}
                <div className="mt-2">
                  <ImageSourceUploadButton
                    inputId="profile_image"
                    accept="image/*"
                    captureFacing="user"
                    onFileSelect={handleProfileImageFile}
                    className="cursor-pointer"
                  >
                    <div className="px-2 w-40 flex flex-row items-center gap-4 text-sm py-2 bg-yellowcolor text-black rounded-lg border border-yellowcolor">
                      <FiUpload size={16} />
                      Click to upload
                    </div>
                  </ImageSourceUploadButton>
                </div>
              </div>

              <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-7">
                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    First Name<span className="text-red-500">*</span>
                  </label>

                  <input
                    type="text"
                    name="firstname"
                    id="firstname"
                    placeholder="Enter your first name"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    {...register("firstname", {
                      ...trimField,
                      required: "First name is required",
                      pattern: personNamePatternRule('First name'),
                      minLength: {
                        value: 1,
                        message: "First name must be at least 3 characters long"
                      }
                    })}
                  />

                  {errors.firstname && <p className="text-red-500 text-xs mt-1">{errors.firstname.message}</p>}
                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Last Name<span className="text-red-500">*</span>
                  </label>

                  <input
                    type="text"
                    name="lastname"
                    id="lastname"
                    placeholder="Enter your last name"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    {...register("lastname", {
                      ...trimField,
                      required: "Last name is required",
                      pattern: personNamePatternRule('Last name'),
                      minLength: {
                        value: 1,
                        message: "Last name must be at least 3 characters long"
                      }
                    })}
                  />

                  {errors.lastname && <p className="text-red-500 text-xs mt-1">{errors.lastname.message}</p>}
                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Email
                  </label>

                  <input
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Enter your email"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    {...register("email", {
                      ...trimField,
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Invalid email format"
                      }
                    })}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>

                <div className="w-full space-y-3">
                  <label htmlFor="phone" className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                    Phone Number
                  </label>
                  <div className="flex items-center">
                    <span className="mr-2 bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 pl-3 pr-6 flex items-center">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/1/19/Flag_of_Ghana.svg" alt="Ghana" className="w-6 h-4 rounded-sm " />
                      <span className="px-2">+233</span>
                    </span>

                    <input
                      type="tel"
                      name="contact"
                      id="contact"
                      {...register("contact", {
                        ...trimField,
                        validate: (value) => {
                          const trimmed = String(value || "").trim()
                          if (!trimmed) return true
                          if (!/^[0-9]+$/.test(trimmed)) {
                            return "Phone number must contain only numbers"
                          }
                          return true
                        }
                      })}
                      placeholder="eg. 0540977343"
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    />
                  </div>

                  {errors.contact && <p className="text-red-500 text-xs mt-1">{errors.contact.message}</p>}
                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Date of Birth
                  </label>

                  <input
                    type="date"
                    name="dob"
                    id="dob"
                    min="1900-01-01"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    {...register("dob", {
                      validate: (value) => {
                        if (!value) return true // Skip validation if empty
                        const today = new Date()
                        const birthDate = new Date(value)
                        if (isNaN(birthDate.getTime())) return true // Skip if invalid date
                        let age = today.getFullYear() - birthDate.getFullYear()
                        const m = today.getMonth() - birthDate.getMonth()
                        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                          age--
                        }
                        return age >= 18 || "You must be at least 18 years old"
                      }
                    })}
                  />
                  {errors.dob && <p className="text-red-500 text-xs mt-1">{errors.dob.message}</p>}

                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Level Of Education
                  </label>

                  <select
                    name="education"
                    id="education"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    {...register("education")}
                  >
                    <option value="">Select</option>
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                    <option value="tertiary">Tertiary</option>
                  </select>
                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Region<span className="text-red-500">*</span>
                  </label>

                  <select
                    id="region"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    value={region || ""}
                    disabled={locationLoading}
                    onChange={(e) => handleRegionChange(e.target.value)}
                  >
                    <option value="">Select Region</option>
                    {regionNames.map((regionName, index) => (
                      <option key={index} value={regionName}>
                        {regionName}
                      </option>
                    ))}
                  </select>
                  <input type="hidden" {...register("region", { required: "Region is required" })} />
                  {errors.region && <p className="text-red-500 text-xs mt-1">{errors.region.message}</p>}
                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    District<span className="text-red-500">*</span>
                  </label>

                  <select
                    id="district"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    value={district || ""}
                    disabled={locationLoading || !region}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                  >
                    <option value="">{locationLoading ? "Loading districts..." : "Select District"}</option>
                    {district && !districts.some((d) => d.name === district) && (
                      <option value={district}>{district}</option>
                    )}
                    {districts.map((item, index) => (
                      <option key={index} value={item.name}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <input type="hidden" {...register("district", { required: "District is required" })} />
                  {errors.district && <p className="text-red-500 text-xs mt-1">{errors.district.message}</p>}
                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Community<span className="text-red-500">*</span>
                  </label>

                  <select
                    name="community"
                    id="community"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white capitalize rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    value={community || ""}
                    disabled={locationLoading || !district}
                    onChange={(e) => setValue("community", e.target.value, { shouldValidate: true })}
                  >
                    <option value="">{locationLoading ? "Loading communities..." : "Select Community"}</option>
                    {community && !communities.includes(community) && (
                      <option className="capitalize" value={community}>{community}</option>
                    )}
                    {communities.map((item, index) => (
                      <option className="capitalize" key={index} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <input type="hidden" {...register("community", { required: "Community is required" })} />

                  {errors.community && <p className="text-red-500 text-xs mt-1">{errors.community.message}</p>}
                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Group (Optional)
                  </label>

                  <select
                    name="group"
                    id="group"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    value={group}
                    onChange={(e) => {
                      setValue("group", e.target.value)
                    }}
                  >
                    <option value="">Select Group</option>
                    {groups?.map((grp) => <option className="capitalize" key={grp?.id} value={grp?.id}>
                      {grp?.group_name}
                    </option>)}
                  </select>
                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Gender<span className="text-red-500">*</span>
                  </label>

                  <div className="flex flex-row items-center gap-2">
                    <div className="flex flex-row items-center gap-2">
                      <input type="radio" name="gender" id="male" value="male" {...register("gender", {
                        required: "Gender is required",
                      })}
                        className="form-radio text-yellowcolor focus:ring-yellowcolor"
                      />
                      <label htmlFor="male" className=" dark:text-white">Male</label>
                    </div>

                    <div className="flex flex-row items-center gap-2">
                      <input
                        type="radio"
                        name="gender"
                        id="female"
                        value="female"
                        {...register("gender", {
                          required: "Gender is required",
                        })}
                        className="form-radio text-yellowcolor focus:ring-yellowcolor"
                      />
                      <label htmlFor="female" className=" dark:text-white">Female</label>
                    </div>
                  </div>

                  {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender.message}</p>}
                </div>

                <div></div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Front Image of Government ID
                  </label>

                  <ImageSourceUploadButton
                    inputId="front_image"
                    accept="image/png,image/jpeg,image/*"
                    captureFacing="environment"
                    onFileSelect={handleGhanaCardFrontFile}
                    className="text-sm h-64 w-full border border-dotted border-gray-200 rounded-md flex flex-col items-center justify-center cursor-pointer"
                  >
                    {frontImagePreview ? (
                      <div className="relative w-full h-full">
                        <img
                          src={frontImagePreview}
                          alt="Ghana Card Front"
                          className="w-full h-full object-cover rounded-lg overflow-hidden"
                        />
                        <div className="absolute bg-black/40 inset-0 rounded-lg overflow-hidden" />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setFrontImagePreview(null)
                            setValue('front_image', null)
                          }}
                          className="absolute bottom-4 left-48 bg-white shadow-2xl text-yellowcolor text-xs font-bold rounded-full p-3"
                        >
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="w-10 h-10 rounded-full bg-yellowcolor/20 flex flex-row items-center justify-center">
                          <div className="w-7 h-7 rounded-full bg-yellowcolor/20 flex flex-row items-center justify-center">
                            <FaUpload size={20} className="text-yellowcolor" />
                          </div>
                        </div>
                        <span className="font-semibold text-yellowcolor">Click to upload</span>
                      </>
                    )}
                  </ImageSourceUploadButton>
                </div>

                <div className="w-full space-y-3">
                  <label className="text-sm font-normal capitalize dark:text-white">
                    Back Image of Government ID
                  </label>

                  <ImageSourceUploadButton
                    inputId="back_image"
                    accept="image/png,image/jpeg,image/*"
                    captureFacing="environment"
                    onFileSelect={handleGhanaCardBackFile}
                    className="text-sm h-64 w-full border border-dotted border-gray-200 rounded-md flex flex-col items-center justify-center cursor-pointer"
                  >
                    {backImagePreview ? (
                      <div className="relative w-full h-full">
                        <img
                          src={backImagePreview}
                          alt="Ghana Card Back"
                          className="w-full h-full object-cover rounded-lg overflow-hidden"
                        />
                        <div className="absolute bg-black/40 inset-0 rounded-lg overflow-hidden" />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setBackImagePreview(null)
                            setValue('back_image', null)
                          }}
                          className="absolute bottom-4 left-48 bg-white shadow-2xl text-yellowcolor text-xs font-bold rounded-full p-3"
                        >
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="w-10 h-10 rounded-full bg-yellowcolor/20 flex flex-row items-center justify-center">
                          <div className="w-7 h-7 rounded-full bg-yellowcolor/20 flex flex-row items-center justify-center">
                            <FaUpload size={20} className="text-yellowcolor" />
                          </div>
                        </div>
                        <span className="font-semibold text-yellowcolor">Click to upload</span>
                      </>
                    )}
                  </ImageSourceUploadButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-8">
          <button type="button" onClick={() => history.goBack()}
            className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'>Cancel</button>
          <button type="submit"
            className='px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg disabled:opacity-50'
            disabled={loading || locationLoading}>
            {loading ? "Updating..." : locationLoading ? "Loading location..." : "Update"}
          </button>
        </div>
      </form>
    </div>
  )
} 